import formatDate from 'date-fns/format';
import formatDuration from 'date-fns/formatDuration';
import isValid from 'date-fns/isValid';
import ruLocale from 'date-fns/locale/ru';
import parseDate from 'date-fns/parse';
import plural from 'plural-ru';

export function noop() {}

export function normalizePhoneNumber(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, '');
}

export function ruPhoneDigitsEqual(a: string, b: string): boolean {
  const da = normalizePhoneNumber(a);
  const db = normalizePhoneNumber(b);
  if (!da || !db) {
    return false;
  }
  if (da === db) {
    return true;
  }
  const ra = da.startsWith('8') ? `7${da.slice(1)}` : da;
  const rb = db.startsWith('8') ? `7${db.slice(1)}` : db;
  if (ra === rb) {
    return true;
  }
  if (ra.length >= 10 && rb.length >= 10 && ra.slice(-10) === rb.slice(-10)) {
    return true;
  }
  return false;
}

export const PENDING_SELECT_PATIENT_PHONE_STORAGE_KEY =
  'pax:pendingSelectPatientPhone';

export function formatMedicinePlural(amount: number): string {
  return plural(amount, 'таблетка', 'таблетки', 'таблеток');
}

/** Склонение «день»: 1 день, 2 дня, 5 дней, 21 день и т.д. */
export function formatDaysPlural(days: number): string {
  const n = Math.abs(days) % 100;
  if (n >= 11 && n <= 14) return 'дней';
  switch (days % 10) {
    case 1:
      return 'день';
    case 2:
    case 3:
    case 4:
      return 'дня';
    default:
      return 'дней';
  }
}

/** Склонение единицы препарата по форме: таблетка/таблетки/таблеток или капсула/капсулы/капсул */
export function formatUnitPlural(amount: number, formType: string): string {
  const lower = (formType || '').toLowerCase();
  if (lower.includes('капс')) {
    return plural(amount, 'капсула', 'капсулы', 'капсул');
  }
  return plural(amount, 'таблетка', 'таблетки', 'таблеток');
}

/** Фраза «по N единица» для инструкции: по одной таблетке (дательный), по 2 таблетки, по 5 таблеток */
export function formatPerDose(amount: number, formType: string): string {
  const lower = (formType || '').toLowerCase();
  if (amount === 1) {
    return lower.includes('капс') ? 'по одной капсуле' : 'по одной таблетке';
  }
  const unit = lower.includes('капс')
    ? plural(amount, 'капсулы', 'капсулы', 'капсул')
    : plural(amount, 'таблетки', 'таблетки', 'таблеток');
  return `по ${amount} ${unit}`;
}

export function formatMoney(amount: number): string {
  return amount.toLocaleString('ru', {
    style: 'currency',
    currency: 'rub',
  });
}

const INVALID_DATE_PLACEHOLDER = '—';

export function formatDateRu(
  ...[date, format, options]: Parameters<typeof formatDate>
): string {
  const d = date instanceof Date ? date : new Date(date as number | string);
  if (!isValid(d)) return INVALID_DATE_PLACEHOLDER;
  return formatDate(d, format, {
    ...options,
    locale: ruLocale,
  });
}

export function toBirthdateInputValue(value?: string): string {
  if (!value) return '';
  const head = value.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  const d = new Date(value);
  if (!isValid(d)) return '';
  return formatDate(d, 'yyyy-MM-dd');
}

export function isoToRuDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

export function ruToIsoDate(ru: string): string | null {
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(ru)) return null;
  const parsed = parseDate(ru, 'dd.MM.yyyy', new Date());
  if (
    !isValid(parsed) ||
    parsed.getFullYear() < 1900 ||
    parsed.getTime() > Date.now()
  ) {
    return null;
  }
  return formatDate(parsed, 'yyyy-MM-dd');
}

export function formatDurationRu(
  ...[duration, options]: Parameters<typeof formatDuration>
): string {
  return formatDuration(duration, {
    ...options,
    locale: ruLocale,
  });
}

export function sum(arr: number[]): number {
  return arr.reduce((acc, curr) => acc + curr, 0);
}

export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Цвета статусов заказов для бейджей и текста (клиент и врач). */
export function getOrderStatusConfig(status: string): {
  bg: string;
  text: string;
  label: string;
} {
  const s = (status || '').trim();
  if (s === 'Отменен') {
    return { bg: 'bg-[#94a3b8]', text: 'text-white', label: 'Отменен' };
  }
  if (s === 'В обработке') {
    return { bg: 'bg-[#cbd5e1]', text: 'text-[#171717]', label: 'В обработке' };
  }
  if (s === 'Требует оплаты') {
    return {
      bg: 'bg-[#facc15]',
      text: 'text-[#171717]',
      label: 'Требует оплаты',
    };
  }
  if (s === 'Принят') {
    return { bg: 'bg-[#22c55e]', text: 'text-white', label: 'Принят' };
  }
  if (s === 'Доставлен') {
    return { bg: 'bg-[#0788f1]', text: 'text-white', label: 'Доставлен' };
  }
  if (s === 'Оплачен') {
    return { bg: 'bg-[#22c55e]', text: 'text-white', label: 'Оплачен' };
  }
  const label =
    s === 'В пути' || s === 'В доставке'
      ? 'В пути'
      : s === 'Сформирован' || s === 'Оформлен'
      ? 'Сформирован'
      : s;
  return {
    bg: 'bg-[#0ea5e9]',
    text: 'text-white',
    label: label || status,
  };
}

const coercePositiveId = (raw: unknown): number | undefined => {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return Math.trunc(raw);
  }
  if (typeof raw === 'string' && /^\d+$/.test(raw)) {
    const n = Number(raw);
    return n > 0 ? n : undefined;
  }
  return undefined;
};

const tryIdFromUnknownRecord = (val: unknown): number | undefined => {
  if (!val || typeof val !== 'object' || Array.isArray(val)) return undefined;
  const o = val as Record<string, unknown>;
  return (
    coercePositiveId(o.id) ??
    coercePositiveId(o.patient_id) ??
    coercePositiveId(o.people_main_id) ??
    coercePositiveId(o.user_id) ??
    coercePositiveId(o.people_id)
  );
};

const NESTED_ID_KEYS = [
  'patient',
  'people',
  'user',
  'person',
  'client',
  'people_main',
] as const;

const findKnownIdDeep = (value: unknown, depth = 0): number | undefined => {
  if (depth > 5 || value == null) return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findKnownIdDeep(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  if (typeof value !== 'object') return undefined;
  const obj = value as Record<string, unknown>;
  const fromKnownKey =
    coercePositiveId(obj.patient_id) ??
    coercePositiveId(obj.people_main_id) ??
    coercePositiveId(obj.user_id) ??
    coercePositiveId(obj.people_id);
  if (fromKnownKey) return fromKnownKey;
  for (const nested of Object.values(obj)) {
    const found = findKnownIdDeep(nested, depth + 1);
    if (found) return found;
  }
  return undefined;
};

export const parseCreatedPatientIdFromResponse = (
  response: unknown,
): number | undefined => {
  if (!response || typeof response !== 'object') return undefined;
  const root = response as Record<string, unknown>;
  const fromRoot =
    coercePositiveId(root.id) ??
    coercePositiveId(root.patient_id) ??
    coercePositiveId(root.people_main_id);
  if (fromRoot) return fromRoot;
  const data = root.data ?? root.result ?? root.payload;
  if (data == null) return undefined;
  if (Array.isArray(data) && data.length > 0) {
    const fromArr = tryIdFromUnknownRecord(data[0]);
    if (fromArr) return fromArr;
  }
  if (typeof data === 'object' && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    const direct =
      coercePositiveId(obj.id) ??
      coercePositiveId(obj.patient_id) ??
      coercePositiveId(obj.people_main_id) ??
      coercePositiveId(obj.user_id) ??
      coercePositiveId(obj.people_id);
    if (direct) return direct;
    for (const k of NESTED_ID_KEYS) {
      if (k in obj) {
        const nested = tryIdFromUnknownRecord(obj[k]);
        if (nested) return nested;
      }
    }
  }
  return coercePositiveId(data) ?? findKnownIdDeep(data);
};

export { patientFormValidation } from './patientFormValidation';
